import * as SecureStore from "expo-secure-store";

/**
 * On-device resume position.
 *
 * Which COURSES are finished is SERVER state (`rn_course_completions`), because
 * that is the evidence the compliance record is built from. Where she is INSIDE
 * a course is not worth a network write per screen: the course is the save
 * point, so the most she can ever lose is one course — about seven minutes —
 * and only on a reinstall or a new device.
 *
 * This covers the case that actually happens: she closes the app mid-course and
 * comes back to the same screen.
 *
 * (The demo build tracked course completion here too. That moved to the server
 * when completion became compliance evidence.)
 */

const KEY = "training.screenPositions";

type Positions = Record<string, number>;
let cache: Positions | null = null;

function key(bundleSlug: string, courseSlug: string): string {
    return `${bundleSlug}/${courseSlug}`;
}

async function load(): Promise<Positions> {
    if (cache) {
        return cache;
    }
    try {
        const raw = await SecureStore.getItemAsync(KEY);
        cache = raw ? (JSON.parse(raw) as Positions) : {};
    } catch {
        cache = {};
    }
    return cache;
}

async function persist(all: Positions): Promise<void> {
    try {
        await SecureStore.setItemAsync(KEY, JSON.stringify(all));
    } catch {
        // Best-effort — the in-memory cache still holds it for this session. A
        // failed write must never block her from continuing the course.
    }
}

/** Where she left off in a course, or 0 if she hasn't started it. */
export async function getScreenPosition(bundleSlug: string, courseSlug: string): Promise<number> {
    const all = await load();
    return all[key(bundleSlug, courseSlug)] ?? 0;
}

export async function setScreenPosition(
    bundleSlug: string,
    courseSlug: string,
    screenIndex: number
): Promise<void> {
    const all = await load();
    all[key(bundleSlug, courseSlug)] = screenIndex;
    await persist(all);
}

/** Clears the saved position once a course is finished. */
export async function clearScreenPosition(bundleSlug: string, courseSlug: string): Promise<void> {
    const all = await load();
    const drop = key(bundleSlug, courseSlug);
    const next = Object.fromEntries(Object.entries(all).filter(([k]) => k !== drop));
    cache = next;
    await persist(next);
}
